import { useEffect, useState, useMemo } from "react";
import "../../assets/css/Deals/DealsDrawer.css";
import api from "../../api/axios";
import Swal from "sweetalert2";
import { MdClose } from "react-icons/md";
import FollowUpModal from "../FollowUp/FollowUpModal";

import ExpensesList from "../Expenses/ExpenseDynamic";
import ExpensesCreate from "../Expenses/ExpensesCreate";
import RenewalCreate from "../Renewal/RenewalsCreate";
import DealsCreate from "../Deals/DealsCreate";
import DealActivityTimeline from "./DealActivityTimeline";
import Pipeline from "../Common/Pipeline";

import {
  FaEdit,
  FaTrashAlt,
  FaPhoneAlt,
  FaMoneyBillWave,
  FaSyncAlt,
  FaBuilding,
  FaUser,
} from "react-icons/fa";
import {
  DEAL_STATUSES,
  DEAL_STAGE_COLORS,
  getDealStageColor,
} from "../Common/DealStatus";

export default function DealsDrawer({
  dealId: propDealId,
  deal: propDeal,
  isOpen,
  onClose,
  refreshDeals,
  initialTab = "overview",
}) {
  const dealId = propDealId || propDeal?.id;
  const [users, setUsers] = useState([]);
  const [deal, setDeal] = useState(propDeal || null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    title: propDeal?.title || "",
    status: propDeal?.status || "",
    deal_amount: propDeal?.deal_amount ?? null,
    assigned_to: propDeal?.assigned_to ?? null,
    actual_amount: propDeal?.actual_amount ?? null,
    expected_expense: propDeal?.expected_expense ?? null,
    actual_expense: propDeal?.actual_expense ?? null,
    expected_close_date: propDeal?.expected_close_date || "",
    closed_date: propDeal?.closed_date || "",
    expected_revenue: propDeal?.lead?.value ?? propDeal?.expected_revenue ?? null,
    expected_profit: propDeal?.profit ?? propDeal?.expected_profit ?? null,
    actual_profit: propDeal?.actual_profit ?? null,
  });
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(initialTab || "overview");
  const [showFollowup, setShowFollowup] = useState(false);
  const [showFollowupModal, setShowFollowupModal] = useState(false);
  const [modals, setModals] = useState({
    followup: false,
    expense: false,
    renewal: false,
    convert: false,
  });

  const [refreshKey, setRefreshKey] = useState(0);

  const currentUser =
    JSON.parse(sessionStorage.getItem("user") || "null") ||
    JSON.parse(localStorage.getItem("user") || "{}");

  const role = currentUser?.role?.toLowerCase();

  const permissions = {
    "super admin": {
      edit: true,
      delete: true,
    },
    super_admin: {
      edit: true,
      delete: true,
    },
    "sales manager": {
      edit: true,
      delete: false,
    },
    admin: {
      edit: true,
      delete: true,
    },
    "sales executive": {
      edit: false,
      delete: false,
    },
    employee: {
      edit: true,
      delete: false,
    },
  };

  const permission = permissions[role] || {
    edit: false,
    delete: false,
  };

  const visiblePipeline = useMemo(() => {
    if (!deal?.pipeline) return [];

    // If the deal is Lost, hide the Won stage
    if (deal.status === "Lost" || deal.status === "Deal Lost") {
      return deal.pipeline.filter(
        (step) => step.stage !== "Won Close" && step.stage !== "Won"
      );
    }

    // Otherwise, hide the Lost stage
    return deal.pipeline.filter(
      (step) => step.stage !== "Lost" && step.stage !== "Deal Lost"
    );
  }, [deal]);

  const stageColors = DEAL_STAGE_COLORS;

  const handlePipelineChange = async (newStage) => {
    try {
      await api.put(`/deal/${dealId}`, {
        status: newStage,
      });

      await fetchDeal();

      refreshDeals?.();

      Swal.fire({
        icon: "success",
        title: `Moved to ${newStage}`,
        timer: 1000,
        showConfirmButton: false,
      });
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Pipeline update failed",
      });
    }
  };
  //fetch users


  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const res = await api.get("/users"); // adjust endpoint if different
        setUsers(res.data || []);
      } catch (err) {
        console.error("Failed to load users", err);
      }
    };

    fetchUsers();
  }, []);


  // ---------------- FETCH DEAL ----------------
  const fetchDeal = async () => {
    if (!dealId) return null;
    try {
      if (!deal) setLoading(true);
      const res = await api.get(`/deal/${dealId}`);
      const data = res.data;
      setDeal(data);

      setFormData({
        title: data.title ?? "",
        status: data.status ?? "",
        deal_amount: data.deal_amount ?? null,
        assigned_to: data.assigned_to ?? null,
        actual_amount: data.actual_amount ?? null,
        expected_expense: data.expected_expense ?? null,
        actual_expense: data.actual_expense ?? null,
        expected_close_date: data.expected_close_date ?? "",
        closed_date: data.closed_date ?? "",
        expected_revenue: data.lead?.value ?? data.expected_revenue ?? null,
        expected_profit: data.profit ?? data.expected_profit ?? null,
        actual_profit: data.actual_profit ?? null,
      });
      return data;
    } catch (err) {
      console.error("Failed to load deal:", err);
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (propDeal) {
      setDeal(propDeal);
      setFormData((prev) => ({
        ...prev,
        title: propDeal.title ?? prev.title,
        status: propDeal.status ?? prev.status,
        deal_amount: propDeal.deal_amount ?? prev.deal_amount,
        assigned_to: propDeal.assigned_to ?? prev.assigned_to,
        actual_amount: propDeal.actual_amount ?? prev.actual_amount,
        expected_expense: propDeal.expected_expense ?? prev.expected_expense,
        actual_expense: propDeal.actual_expense ?? prev.actual_expense,
        expected_close_date: propDeal.expected_close_date ?? prev.expected_close_date,
        closed_date: propDeal.closed_date ?? prev.closed_date,
        expected_revenue: propDeal.lead?.value ?? propDeal.expected_revenue ?? prev.expected_revenue,
        expected_profit: propDeal.profit ?? propDeal.expected_profit ?? prev.expected_profit,
        actual_profit: propDeal.actual_profit ?? prev.actual_profit,
      }));
    }
  }, [propDeal]);

  useEffect(() => {
    if (!dealId || !isOpen) return;
    setEditMode(false);
    setActiveTab(initialTab || "overview");
    setErrors({});
    fetchDeal();
  }, [dealId, initialTab, isOpen]);


  //validation for update form


  const [errors, setErrors] = useState({
    title: "",
    status: "",
    deal_amount: "",
    expected_expense: "",
    actual_expense: "",
    expected_close_date: "",
    closed_date: "",
    assigned_to: "",
  });


  const validateForm = () => {
    const newErrors = {};

    // Deal Title
    if (!formData.title?.trim()) {
      newErrors.title = "Deal name is required";
    } else if (formData.title.trim().length > 120) {
      newErrors.title = "Deal name too long";
    }

    // Status
    if (!formData.status) {
      newErrors.status = "Status is required";
    }

    // Deal Amount (Actual Revenue)
    if (formData.deal_amount === null || formData.deal_amount === "") {
      newErrors.deal_amount = "Actual revenue is required";
    } else if (Number(formData.deal_amount) < 0) {
      newErrors.deal_amount = "Amount cannot be negative";
    }

    // Expected Expense
    if (
      formData.expected_expense !== null &&
      formData.expected_expense < 0
    ) {
      newErrors.expected_expense = "Cannot be negative";
    }

    // Actual Expense
    if (
      formData.actual_expense !== null &&
      formData.actual_expense < 0
    ) {
      newErrors.actual_expense = "Cannot be negative";
    }

    // Date validation
    if (
      formData.expected_close_date &&
      formData.closed_date &&
      new Date(formData.closed_date) < new Date(formData.expected_close_date)
    ) {
      newErrors.closed_date =
        "Actual close date cannot be before expected close date";
    }

    // Assigned To
    if (!formData.assigned_to) {
      newErrors.assigned_to = "Assign a user";
    }

    setErrors(newErrors);

    return Object.keys(newErrors).length === 0;
  };

  if (!isOpen || !dealId) return null;

  // ---------------- HANDLERS ----------------
  const handleChange = (e) => {
    const { name, value } = e.target;

    const numberFields = [
      "deal_amount",
      "actual_amount",
      "assigned_to",
      "profit",
      "expected_profit",
      "actual_profit",
      "expected_expense",
      "actual_expense",
      "expected_revenue",
    ];

    setFormData((prev) => ({
      ...prev,
      [name]: numberFields.includes(name)
        ? value === ""
          ? null
          : Number(value)
        : value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };




  const toggleModal = (key, value) => {
    setModals((p) => ({ ...p, [key]: value }));
  };

  // ---------------- UPDATE ----------------
  const updateDeal = async () => {
    if (!validateForm()) return;

    try {
      setLoading(true);

      const payload = {
        ...formData,
        deal_amount: formData.deal_amount ?? null,
        actual_amount: formData.actual_amount ?? null,
        expiry_date: formData.expiry_date ?? null,
      };

      await api.put(`/deal/${dealId}`, payload);

      await Swal.fire({
        icon: "success",
        title: "Deal Updated",
        timer: 1200,
        showConfirmButton: false,
      });

      setEditMode(false);
      fetchDeal();
      refreshDeals?.();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Update Failed" });
    } finally {
      setLoading(false);
    }
  };

  // ---------------- DELETE ----------------
  const deleteDeal = async () => {
    const res = await Swal.fire({
      title: "Delete this deal?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#dc2626",
      confirmButtonText: "Delete",
    });

    if (!res.isConfirmed) return;

    try {
      await api.delete(`/deal/${dealId}`);

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        timer: 1200,
        showConfirmButton: false,
      });

      refreshDeals?.();
      onClose();
    } catch (err) {
      Swal.fire({ icon: "error", title: "Delete Failed" });
    }
  };

  const stageColor = (stage) => getDealStageColor(stage);



  const getInitials = (name = "") => {
    return name
      .split(" ")
      .filter(Boolean)
      .map((word) => word[0])
      .join("")
      .substring(0, 2)
      .toUpperCase() || "DL";
  };

  return (
    <div className="drawer-overlay deal-overlay" onClick={onClose}>
      <div className="lead-drawer deal-drawer" onClick={(e) => e.stopPropagation()}>

        {/* ================= HEADER (SAME AS LEAD) ================= */}
        <div className="drawer-header">

          <div className="drawer-top">

            <div className="header-left">

              <div className="lead-header-info">

                <div className="lead-avatar">
                  {getInitials(formData.title || deal?.title)}
                </div>

                <div className="lead-info">

                  <div className="lead-title-row">
                    <h2 className="deal-title-e">
                      {formData.title || deal?.title || "Untitled Deal"}
                    </h2>

                    <div className="lead-badges">
                      <span className="status-badge">{formData.status || deal?.status || "Open"}</span>
                      {deal?.lead?.lead_type && (
                        <span className="type-badge">{deal.lead.lead_type}</span>
                      )}
                    </div>
                  </div>

                  <div className="drawer-bottom">
                    <button className="action-btn secondary" onClick={() => toggleModal("followup", true)}>
                      <FaPhoneAlt size={12} />
                      Follow Up
                    </button>

                    <button className="action-btn secondary" onClick={() => toggleModal("expense", true)}>
                      <FaMoneyBillWave size={12} />
                      Expense
                    </button>

                    <button className="action-btn secondary" onClick={() => toggleModal("renewal", true)}>
                      <FaSyncAlt size={12} />
                      Renewal
                    </button>
                  </div>

                </div>

              </div>

            </div>

            <div className="header-right">

              {!editMode && (
                <div className="header-icon-actions">
                  {permission.edit && (
                    <button
                      className="icon-btn edit-btn"
                      aria-label="edit deal"
                      onClick={() => setEditMode(true)}
                    >
                      <FaEdit />
                    </button>

                  )}

                  {permission.delete && (
                    <button
                      className="icon-btn delete-btn"
                      aria-label="delete deal"
                      onClick={deleteDeal}
                    >
                      <FaTrashAlt />
                    </button>

                  )}
                </div>
              )}

              <button
                className="close-btn"
                aria-label="close drawer"
                onClick={onClose}
              >
                <MdClose />
              </button>


            </div>

          </div>

        </div>

        {/* ================= TABS ================= */}
        <div className="drawer-tabs-l">
          <div className="drawer-tabs-top">
            <button
              className={activeTab === "overview" ? "active-tab" : ""}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </button>

            <button
              className={activeTab === "timeline" ? "active-tab" : ""}
              onClick={() => setActiveTab("timeline")}
            >
              Timeline
            </button>

            <button
              className={activeTab === "expenses" ? "active-tab" : ""}
              onClick={() => setActiveTab("expenses")}
            >
              Expenses
            </button>
          </div>
        </div>

        {/* ================= BODY ================= */}
        <div className="drawer-section">

          <div className="drawer-content">

            {/* ================= OVERVIEW ================= */}
            {activeTab === "overview" && (
              <>
                {/* Contextual Stage Prompt Banners */}
                {(deal?.status === "Vendor PO" || deal?.status === "vendor_po") && (
                  <div className="deal-stage-prompt-banner vendor-po">
                    <div className="deal-stage-prompt-content">
                      <span className="deal-stage-prompt-icon">📄</span>
                      <div>
                        <h4 className="deal-stage-prompt-title">Stage: Vendor PO</h4>
                        <p className="deal-stage-prompt-desc">Attach client purchase order (PO) or vendor PO document via Follow-Up.</p>
                      </div>
                    </div>
                    <button
                      className="btn-stage-upload"
                      onClick={() => toggleModal("followup", true)}
                    >
                      Log Vendor PO
                    </button>
                  </div>
                )}

                {/* {(deal?.status === "Client Invoicing" || deal?.status === "client_invoicing") && (
                  <div className="deal-stage-prompt-banner client-invoice">
                    <div className="deal-stage-prompt-content">
                      <span className="deal-stage-prompt-icon">🧾</span>
                      <div>
                        <h4 className="deal-stage-prompt-title">Stage: Client Invoicing</h4>
                        <p className="deal-stage-prompt-desc">Attach client tax invoice or billing document via Follow-Up.</p>
                      </div>
                    </div>
                    <button
                      className="btn-stage-upload"
                      onClick={() => toggleModal("followup", true)}
                    >
                      Log Invoicing
                    </button>
                  </div>
                )} */}
                {/* PIPELINE */}
                <Pipeline steps={visiblePipeline} />

                {/* DETAILS GRID */}
                <div className="detail-grid">

                  <div>
                    <label>Deal Name</label>

                    {editMode ? (
                      <>
                        <input
                          name="title"
                          value={formData.title || ""}
                          onChange={handleChange}
                          className={errors.title ? "error" : ""}
                        />

                        {errors.title && (
                          <span className="error-text">{errors.title}</span>
                        )}
                      </>
                    ) : (
                      <p>{formData.title || deal?.title || "-"}</p>
                    )}
                  </div>

                  <div>
                    <label>Status</label>

                    {editMode ? (
                      <>
                        <select
                          name="status"
                          value={formData.status || ""}
                          onChange={handleChange}
                          className={errors.status ? "error" : ""}
                        >
                          {!DEAL_STATUSES.includes(formData.status) && formData.status && (
                            <option value={formData.status}>{formData.status}</option>
                          )}
                          {DEAL_STATUSES.map((status) => (
                            <option key={status} value={status}>
                              {status}
                            </option>
                          ))}
                        </select>

                        {errors.status && (
                          <span className="error-text">{errors.status}</span>
                        )}
                      </>
                    ) : (
                      <p>{formData.status || deal?.status || "-"}</p>
                    )}
                  </div>

                  <div>
                    <label>Deal Amount</label>

                    {editMode ? (
                      <>
                        <input
                          type="number"
                          name="deal_amount"
                          value={formData.deal_amount ?? ""}
                          onChange={handleChange}
                          className={errors.deal_amount ? "error" : ""}
                        />

                        {errors.deal_amount && (
                          <span className="error-text">{errors.deal_amount}</span>
                        )}
                      </>
                    ) : (
                      <p>₹{Number(formData.deal_amount ?? deal?.deal_amount ?? 0).toLocaleString("en-IN")}</p>
                    )}
                  </div>

                  <div>
                    <label>Expected Revenue</label>

                    {editMode ? (
                      <input
                        type="number"
                        name="expected_revenue"
                        value={formData.expected_revenue ?? ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <p>₹{Number(formData.expected_revenue || 0).toLocaleString("en-IN")}</p>
                    )}
                  </div>

                  <div>
                    <label>Expected Expense</label>

                    {editMode ? (
                      <>
                        <input
                          type="number"
                          name="expected_expense"
                          value={formData.expected_expense ?? ""}
                          onChange={handleChange}
                          className={errors.expected_expense ? "error" : ""}
                        />

                        {errors.expected_expense && (
                          <span className="error-text">{errors.expected_expense}</span>
                        )}
                      </>
                    ) : (
                      <p>₹{Number(formData.expected_expense || 0).toLocaleString("en-IN")}</p>
                    )}
                  </div>

                  <div>
                    <label>Actual Expense</label>

                    {editMode ? (
                      <>
                        <input
                          type="number"
                          name="actual_expense"
                          value={formData.actual_expense ?? ""}
                          onChange={handleChange}
                          className={errors.actual_expense ? "error" : ""}
                        />

                        {errors.actual_expense && (
                          <span className="error-text">{errors.actual_expense}</span>
                        )}
                      </>
                    ) : (
                      <p>₹{Number(formData.actual_expense || 0).toLocaleString("en-IN")}</p>
                    )}
                  </div>

                  <div>
                    <label>Expected Profit</label>

                    {editMode ? (
                      <input
                        type="number"
                        name="expected_profit"
                        value={formData.expected_profit ?? ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <p>₹{Number(formData.expected_profit || 0).toLocaleString("en-IN")}</p>
                    )}
                  </div>

                  <div>
                    <label>Actual Profit</label>

                    {editMode ? (
                      <input
                        type="number"
                        name="actual_profit"
                        value={formData.actual_profit ?? ""}
                        onChange={handleChange}
                      />
                    ) : (
                      <p>₹{Number(formData.actual_profit || 0).toLocaleString("en-IN")}</p>
                    )}
                  </div>

                  <div>
                    <label>Expected Close Date</label>

                    {editMode ? (
                      <>
                        <input
                          type="date"
                          name="expected_close_date"
                          value={formData.expected_close_date || ""}
                          onChange={handleChange}
                        />
                      </>
                    ) : (
                      <p>
                        {formData.expected_close_date
                          ? new Date(formData.expected_close_date).toLocaleDateString()
                          : "-"}
                      </p>
                    )}
                  </div>

                  <div>
                    <label>Actual Close Date</label>

                    {editMode ? (
                      <>
                        <input
                          type="date"
                          name="closed_date"
                          value={formData.closed_date || ""}
                          onChange={handleChange}
                          className={errors.closed_date ? "error" : ""}
                        />

                        {errors.closed_date && (
                          <span className="error-text">{errors.closed_date}</span>
                        )}
                      </>
                    ) : (
                      <p>
                        {formData.closed_date
                          ? new Date(formData.closed_date).toLocaleDateString()
                          : "-"}
                      </p>
                    )}
                  </div>

                  <div>
                    <label>Assigned To</label>

                    {editMode ? (
                      <>
                        <select
                          name="assigned_to"
                          value={formData.assigned_to || ""}
                          onChange={handleChange}
                          className={errors.assigned_to ? "error" : ""}
                        >
                          <option value="">Unassigned</option>

                          {users.map((user) => (
                            <option key={user.id} value={user.id}>
                              {user.name}
                            </option>
                          ))}
                        </select>

                        {errors.assigned_to && (
                          <span className="error-text">{errors.assigned_to}</span>
                        )}
                      </>
                    ) : (
                      <p>
                        {users.find((u) => u.id === formData.assigned_to)?.name || "-"}
                      </p>
                    )}
                  </div>

                </div>

                {editMode && (
                  <div className="overview-actions">

                    <button
                      className="btn-secondary"
                      onClick={() => {
                        setFormData({
                          title: deal?.title ?? "",
                          status: deal?.status ?? "",
                          deal_amount: deal?.deal_amount ?? null,
                          assigned_to: deal?.assigned_to ?? null,
                          actual_amount: deal?.actual_amount ?? null,
                        });

                        setEditMode(false);
                      }}
                    >
                      Cancel
                    </button>

                    <button
                      className="btn-primary"
                      onClick={updateDeal}
                      disabled={loading}
                    >
                      {loading ? "Saving..." : "Save Changes"}
                    </button>

                  </div>
                )}

                {/* ORGANIZATION */}
                <div className="info-section">
                  <div className="section-title">
                    <FaBuilding />
                    <span>Organization</span>
                  </div>

                  {deal?.organization ? (
                    <div className="crm-card">
                      <div className="crm-card-icon building">
                        <FaBuilding />
                      </div>

                      <div className="crm-card-content">
                        <h4>{deal.organization.name}</h4>
                        <p>{deal.organization.website}</p>

                        <div className="crm-meta">
                          <span>{deal.organization.country}</span>
                          <span>{deal.organization.state}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-card">No Organization Linked</div>
                  )}
                </div>

                {/* CONTACT */}
                <div className="info-section">
                  <div className="section-title">
                    <FaUser />
                    <span>Contact</span>
                  </div>

                  {deal?.contact ? (
                    <div className="crm-card">
                      <div className="crm-card-icon">
                        <FaUser />
                      </div>

                      <div className="crm-card-content">
                        <h4>
                          {deal.contact.first_name} {deal.contact.last_name}
                        </h4>

                        <p>{deal.contact.designation}</p>

                        <div className="crm-meta">
                          <span>{deal.contact.email}</span>
                          <span>{deal.contact.phone}</span>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="empty-card">No Contact Linked</div>
                  )}
                </div>

              </>
            )}

            {/* ================= TIMELINE ================= */}
            {activeTab === "timeline" && (
              <DealActivityTimeline
                dealId={dealId}
                deal={deal}
                refreshKey={refreshKey}
              />
            )}

            {/* ================= EXPENSES ================= */}
            {activeTab === "expenses" && (
              <div className="expenses-tab">
                <ExpensesList
                  leadId={deal?.lead?.id}
                  dealId={dealId}
                  organizationId={deal?.organization?.id}
                  refreshKey={refreshKey}
                  onAddExpense={() => toggleModal("expense", true)}
                />
              </div>
            )}

          </div>
        </div>

        {/* ================= MODALS ================= */}

        {modals.followup && (
          <FollowUpModal
            type="deal"
            id={dealId}
            usersList={users}
            isOpen={modals.followup}
            onClose={() => toggleModal("followup", false)}
            onSuccess={async () => {
              toggleModal("followup", false);

              try {
                await fetchDeal(); // refresh drawer data
              } catch (e) {
                console.error("Failed to re-fetch deal:", e);
              }
              try {
                await refreshDeals?.(); // refresh deal list
              } catch (e) {
                console.error("Failed to refresh deals:", e);
              }
              setRefreshKey((p) => p + 1);
            }}
          />
        )}

        {modals.expense && (
          <ExpensesCreate
            dealId={dealId}
            organizationId={deal?.organization?.id}
            isOpen={modals.expense}
            onClose={() => toggleModal("expense", false)}
            onSuccess={() => {
              toggleModal("expense", false);
              fetchDeal();
              setRefreshKey((p) => p + 1);
            }}
          />
        )}

        {modals.renewal && (
          <RenewalCreate
            dealId={dealId}
            organizationId={deal?.organization?.id}
            isOpen={modals.renewal}
            onClose={() => toggleModal("renewal", false)}
            onSuccess={() => toggleModal("renewal", false)}
          />
        )}

        {modals.convert && (
          <DealsCreate
            isOpen={modals.convert}
            onClose={() => toggleModal("convert", false)}
            lead={deal?.lead}
            onSuccess={() => toggleModal("convert", false)}
          />
        )}

      </div>
    </div>
  );
}
